import React, { createContext, useContext, useReducer, useEffect, useRef, useCallback } from 'react';
import { goldApi } from '../../services/api';

const DashboardContext = createContext();

const initialState = {
dashboardData: null,
todayGoldRate: null,
loading: true,
error: null,
refreshing: false,
notifications: [],
unreadNotifications: 0,
inventorySummary: null,
lowStockItems: [],
customers: []
};

function dashboardReducer(state, action) {
switch (action.type) {
case 'SET_DATA':
return { ...state, dashboardData: action.payload, loading: false, error: null };
case 'SET_GOLD_RATE':
return { ...state, todayGoldRate: action.payload };
case 'SET_INVENTORY_SUMMARY':
return { ...state, inventorySummary: action.payload };
case 'SET_LOW_STOCK_ITEMS':
return { ...state, lowStockItems: action.payload };
case 'SET_CUSTOMERS':
return { ...state, customers: action.payload };
case 'SET_LOADING':
return { ...state, loading: action.payload };
case 'SET_ERROR':
return { ...state, error: action.payload, loading: false };
case 'SET_REFRESHING':
return { ...state, refreshing: action.payload };
case 'ADD_NOTIFICATION':
const newNotifications = [action.payload, ...state.notifications.slice(0, 9)];
return {
...state,
notifications: newNotifications,
unreadNotifications: state.unreadNotifications + 1
};
case 'MARK_NOTIFICATIONS_READ':
return { ...state, unreadNotifications: 0 };
case 'CLEAR_NOTIFICATIONS':
return { ...state, notifications: [], unreadNotifications: 0 };
case 'SET_SAMPLE_DATA':
return {
...state,
dashboardData: action.payload.dashboardData,
todayGoldRate: action.payload.todayGoldRate,
loading: false,
error: null
};
case 'RESET':
return initialState;
default:
return state;
}
}

// Generate sample data for fallback
const generateSampleData = () => ({
stats: {
total_inventory_value: 1250000,
today_sales: 150000,
today_purchases: 75000,
total_customers: 245,
total_transactions: 1250,
low_stock_items: 3,
total_gold_weight: 2500,
today_expenses: 5000,
cash_in_hand: 500000,
total_debt: 150000
},
transactions: generateSampleTransactions(),
inventory: {
by_karat: [
{ karat: '24K', weight: 1000 },
{ karat: '22K', weight: 800 },
{ karat: '18K', weight: 400 },
{ karat: '14K', weight: 300 },
]
},
trends: {
sales: 15,
purchases: 12,
customers: 8,
inventory_value: 5
}
});

const generateSampleGoldRate = () => ({
date: new Date().toISOString(),
price_per_gram: 3500,
gold_24k_per_gram: 3500,
gold_22k_per_gram: 3200,
price_per_tola: 40800,
gold_24k_per_tola: 40800,
silver_per_gram: 40
});

const generateSampleTransactions = () => {
const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
return days.map(day => ({
date: day,
sales: Math.floor(Math.random() * 200000) + 100000,
purchases: Math.floor(Math.random() * 100000) + 50000
}));
};

export function DashboardProvider({ children }) {
const [state, dispatch] = useReducer(dashboardReducer, initialState);
const abortControllerRef = useRef(null);

// Cancel previous requests and prepare a controller for the next request.
const cancelRequests = useCallback(() => {
if (abortControllerRef.current) {
abortControllerRef.current.abort();
}
abortControllerRef.current = new AbortController();
}, []);

// DashboardProvider.js - Updated safeApiCall function
const safeApiCall = useCallback(async (apiFunction, params = {}, fallbackValue = null) => {
try {
console.log(`Calling API: ${apiFunction.name}`);
const response = await apiFunction({
...params,
signal: abortControllerRef.current?.signal
});

console.log('API Response:', response);

// Check if response is valid and has data
if (response && response.data !== undefined) {
return response.data;
}

// Check if response is HTML (error page)
if (response && response.config && response.headers) {
const contentType = response.headers['content-type'] || response.headers['Content-Type'];
if (contentType && contentType.includes('text/html')) {
console.warn('API returned HTML instead of JSON. Check server configuration.');
return fallbackValue;
}
}

return fallbackValue;

} catch (error) {
if (error.name === 'AbortError') {
console.log('Request aborted');
return fallbackValue;
}

// Check if error response is HTML
if (error.response) {
const contentType = error.response.headers?.['content-type'];
if (contentType && contentType.includes('text/html')) {
console.warn('Server returned HTML error page:', error.response.status);
return fallbackValue;
}

// Handle specific HTTP errors
if (error.response.status === 404) {
console.warn('API endpoint not found:', error.config?.url);
} else if (error.response.status === 500) {
console.warn('Server error on API call');
}
} else if (error.request) {
console.warn('No response received from server. Check API server.');
} else {
console.warn('Error setting up request:', error.message);
}

return fallbackValue;
}
}, []);

// Set sample data when the API is unavailable or demo mode is enabled.
const setDemoData = useCallback(() => {
dispatch({
type: 'SET_SAMPLE_DATA',
payload: {
dashboardData: generateSampleData(),
todayGoldRate: generateSampleGoldRate()
}
});

// Add notification about demo mode
dispatch({
type: 'ADD_NOTIFICATION',
payload: {
id: Date.now(),
type: 'info',
title: 'Demo Mode Active',
message: 'Using sample data. Connect to API for real-time data.',
time: new Date().toISOString(),
read: false
}
});
}, []);

// Update the fetchDashboardData function in DashboardProvider.js
const fetchDashboardData = useCallback(async (forceDemo = false) => {
try {
dispatch({ type: 'SET_LOADING', payload: true });
dispatch({ type: 'SET_ERROR', payload: null });

cancelRequests();

// For debugging - check if we should force demo mode
if (forceDemo || process.env.REACT_APP_FORCE_DEMO === 'true') {
console.log('Using demo mode (forced)');
setDemoData();
return;
}

// Test API connection first
try {
const testResponse = await goldApi.getHealth();
console.log('API Health Check:', testResponse.data);

if (!testResponse.data?.healthy) {
throw new Error('API health check failed');
}
} catch (healthError) {
console.warn('API health check failed, using demo mode');
setDemoData();
return;
}

// Fetch real data
const [dashboardData, goldRateData, inventoryData, lowStockData] = await Promise.all([
safeApiCall(goldApi.getDashboardStats, {}, null),
safeApiCall(goldApi.getTodayGoldRate, {}, null),
safeApiCall(goldApi.getInventorySummary, {}, null),
safeApiCall(goldApi.getLowStockItems, {}, null)
]);

console.log('Fetched Data:', {
dashboard: !!dashboardData,
goldRate: !!goldRateData,
inventory: !!inventoryData,
lowStock: lowStockData?.length || 0
});

// Determine if we have any real data
const hasRealData = dashboardData || goldRateData || inventoryData;

if (hasRealData) {
// Use whatever real data we got, fallback to sample for missing parts
dispatch({
type: 'SET_DATA',
payload: dashboardData || generateSampleData()
});

if (goldRateData) {
dispatch({ type: 'SET_GOLD_RATE', payload: goldRateData });
} else {
dispatch({ type: 'SET_GOLD_RATE', payload: generateSampleGoldRate() });
}

if (inventoryData) {
dispatch({ type: 'SET_INVENTORY_SUMMARY', payload: inventoryData });
}

if (lowStockData && Array.isArray(lowStockData)) {
dispatch({ type: 'SET_LOW_STOCK_ITEMS', payload: lowStockData });
}

// Show partial data warning if some endpoints failed
if (!dashboardData || !goldRateData) {
dispatch({
type: 'ADD_NOTIFICATION',
payload: {
id: Date.now(),
type: 'warning',
title: 'Partial Data Loaded',
message: 'Some data loaded successfully, but some API endpoints failed.',
time: new Date().toISOString(),
read: false
}
});
}
} else {
// No real data available, use demo
console.log('No API data received, using demo mode');
setDemoData();
}

} catch (error) {
console.error('Dashboard fetch error:', error);
setDemoData();
}
}, [safeApiCall, setDemoData, cancelRequests]);

useEffect(() => {
fetchDashboardData();

// Set up auto-refresh every 5 minutes
const refreshInterval = setInterval(() => {
console.log('Auto-refreshing dashboard data...');
fetchDashboardData();
}, 5 * 60 * 1000); // 5 minutes

return () => {
clearInterval(refreshInterval);
if (abortControllerRef.current) {
abortControllerRef.current.abort();
}
};
}, [fetchDashboardData]);

const refreshDashboard = useCallback(async () => {
dispatch({ type: 'SET_REFRESHING', payload: true });
await fetchDashboardData();
dispatch({ type: 'SET_REFRESHING', payload: false });
}, [fetchDashboardData]);

const addNotification = useCallback((notification) => {
dispatch({ type: 'ADD_NOTIFICATION', payload: notification });
}, []);

const markNotificationsAsRead = useCallback(() => {
dispatch({ type: 'MARK_NOTIFICATIONS_READ' });
}, []);

const clearNotifications = useCallback(() => {
dispatch({ type: 'CLEAR_NOTIFICATIONS' });
}, []);

const value = {
...state,
refreshDashboard,
addNotification,
markNotificationsAsRead,
clearNotifications,
isDemoMode: !state.dashboardData || state.dashboardData.stats?.total_inventory_value === 1250000 // Check if using sample data
};

return (
<DashboardContext.Provider value={value}>
{children}
</DashboardContext.Provider>
);
}

export const useDashboard = () => {
const context = useContext(DashboardContext);
if (!context) {
throw new Error('useDashboard must be used within DashboardProvider');
}
return context;
};
